"use client"
import { type CSSProperties, useRef } from "react"
import { useGSAP } from "@gsap/react"
import cn from "classnames"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

import { AnimateText } from "~/components/elements/AnimateText/AnimateText"
import { FadeIn } from "~/components/elements/FadeIn/FadeIn"
import { useScroll } from "~/components/foundations/ScrollProvider"
import { Button } from "~/components/primitives/Button/Button"
import { HtmlRenderer } from "~/components/primitives/HtmlRenderer"
import { ResponsiveAsset } from "~/components/primitives/ResponsiveAsset/ResponsiveAsset"
import { useMatchMedia } from "~/hooks/useMatchMedia"
import { getModuleConfigProps } from "~/utils/moduleConfig"

import type { SideBySideFloatingImageInterface } from "./SideBySideFloatingImage.interface"
import s from "./SideBySideFloatingImage.module.scss"

gsap.registerPlugin(ScrollTrigger)

export type SideBySideFloatingImageViewProps = Omit<
  SideBySideFloatingImageInterface,
  "id"
> & {
  moduleIndex: number
  className?: string
  style?: CSSProperties
}

export const SideBySideFloatingImageView = ({
  floatingImage,
  cardBackground,
  headline,
  body,
  cardHeadline,
  cardBody,
  imageRight,
  cta,
  config,
  moduleIndex,
  className,
  style,
}: SideBySideFloatingImageViewProps) => {
  const configProps = getModuleConfigProps(config)
  const { lenis } = useScroll()
  const sectionRef = useRef<HTMLElement>(null)
  const floatingImageRef = useRef<HTMLDivElement>(null)
  const isMd = useMatchMedia("md")

  useGSAP(
    () => {
      if (!lenis) return

      const mm = gsap.matchMedia()

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        if (!floatingImageRef.current) return

        gsap.fromTo(
          floatingImageRef.current,
          { y: 100 },
          {
            y: isMd ? -100 : 0,
            ease: "none",
            scrollTrigger: {
              trigger: sectionRef.current,
              scroller: lenis.rootElement,
              start: "top bottom",
              end: "bottom top",
              scrub: true,
            },
          },
        )
      })
    },
    { scope: sectionRef, dependencies: [lenis, isMd] },
  )

  return (
    <section
      ref={sectionRef}
      className={cn(
        s.sideBySideFloatingImage,
        `stack-module-${moduleIndex}`,
        className,
      )}
      data-module="side-by-side-floating-image"
      data-image-right={imageRight}
      {...configProps}
      style={{ ...configProps.style, ...style }}
    >
      <div className={cn(s.inner)}>
        <div className={s.textCol} data-debug="leftCard">
          {headline && (
            <FadeIn delay={0.1}>
              <AnimateText element="h2" className={s.headline}>
                {headline}
              </AnimateText>
            </FadeIn>
          )}
          {body && (
            <HtmlRenderer
              animate
              html={body}
              element="div"
              className={s.body}
              delay={0.2}
            />
          )}
        </div>

        <div className={s.collage} data-debug="rightCard">
          <div className={s.floatingImageWrapper}>
            <div
              ref={floatingImageRef}
              className={s.floatingImage}
              data-debug="floatingImage"
            >
              {floatingImage?.desktop && (
                <ResponsiveAsset
                  className={s.floatingAsset}
                  desktop={floatingImage.desktop}
                  mobile={floatingImage.mobile ?? undefined}
                  imageSizes={{ base: "100vw", md: "35vw" }}
                  animateOnLoad={false}
                />
              )}
            </div>
          </div>

          <div className={s.card} data-debug="card">
            {cardBackground?.desktop && (
              <div className={s.cardBg}>
                <ResponsiveAsset
                  className={s.cardBgAsset}
                  desktop={cardBackground.desktop}
                  mobile={cardBackground.mobile ?? undefined}
                  imageSizes={{ base: "100vw", md: "60vw" }}
                  animateOnLoad={false}
                />
              </div>
            )}
            <div
              className={s.cardOverlay}
              aria-hidden="true"
              data-debug="cardOverlay"
            />
            <div className={s.cardContent} data-debug="cardContent">
              {cardHeadline && (
                <FadeIn delay={0.1}>
                  <AnimateText element="h3" className={s.cardHeadline}>
                    <HtmlRenderer inline html={cardHeadline} />
                  </AnimateText>
                </FadeIn>
              )}
              {cardBody && (
                <HtmlRenderer
                  animate
                  html={cardBody}
                  element="div"
                  className={s.cardBody}
                />
              )}
              {cta && (
                <Button
                  cta={{ ...cta, variant: "tertiary" }}
                  theme="dark"
                  className={s.cta}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
